import { useMutation } from '@apollo/client/react';
import { Box, Paper, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useNotify } from 'react-admin';

import {
  AdminRenameClientLaunchDocument,
  AdminSetClientLaunchWaitingOnDocument,
  ClientLaunchType,
} from '../../../graphql/types';
import { WARNING } from '../../../theme';
import { autoLaunchName, REVIEW_STEP_ID } from './launchState';
import type {
  ClientLaunch,
  LaunchStep,
  SetupChoices,
  StepStatus,
} from './types';

const WAITING_ON_SAVE_DELAY_MS = 800;

const Heading: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({
  children,
  htmlFor,
}) => (
  <Typography
    component={htmlFor ? 'label' : 'h2'}
    htmlFor={htmlFor}
    sx={{
      display: 'block',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const WaitingOnField: React.FC<{ launch: ClientLaunch }> = ({ launch }) => {
  const notify = useNotify();
  const [setWaitingOn] = useMutation(AdminSetClientLaunchWaitingOnDocument);
  const [value, setValue] = React.useState(launch.waitingOn ?? '');
  const savedValue = React.useRef(launch.waitingOn ?? '');
  const wantedValue = React.useRef(launch.waitingOn ?? '');
  const saving = React.useRef(false);

  // One save at a time: after each, the latest value is sent if it differs
  // from what was saved, so a change made during a save isn't lost. A failed
  // save stops here, and the next edit or leaving the field tries again.
  const save = React.useCallback(
    async (waitingOn: string) => {
      wantedValue.current = waitingOn;
      if (saving.current) {
        return;
      }
      saving.current = true;
      try {
        while (wantedValue.current !== savedValue.current) {
          const next = wantedValue.current;
          await setWaitingOn({
            variables: { input: { id: launch.id, waitingOn: next } },
          });
          savedValue.current = next;
        }
      } catch (error) {
        notify(`Failed to save Waiting on: ${errorMessage(error)}`, {
          type: 'error',
        });
      } finally {
        saving.current = false;
      }
    },
    [launch.id, setWaitingOn, notify],
  );

  // Saves as you type, so a blocker is recorded even when the step can't be
  // saved yet.
  React.useEffect(() => {
    const timer = setTimeout(() => save(value), WAITING_ON_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value, save]);

  return (
    <Box>
      <Heading htmlFor="launch-waiting-on">Waiting on</Heading>
      <TextField
        id="launch-waiting-on"
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => save(value)}
        placeholder="Nothing. Add a note when you're blocked, e.g. Client: office ID list"
        helperText="Shown on the overview. Clear it when nothing's blocking."
      />
    </Box>
  );
};

const LaunchNameField: React.FC<{ launch: ClientLaunch }> = ({ launch }) => {
  const notify = useNotify();
  const [renameLaunch] = useMutation(AdminRenameClientLaunchDocument);
  const [value, setValue] = React.useState(launch.name ?? '');
  const defaultName = autoLaunchName(launch.features);

  const handleBlur = async () => {
    // A launch can't have a blank name, so clearing it falls back to the
    // feature names.
    const name = value.trim() || defaultName;
    setValue(name);
    if (!name || name === launch.name) {
      return;
    }
    try {
      await renameLaunch({ variables: { input: { id: launch.id, name } } });
    } catch (error) {
      notify(`Failed to rename the launch: ${errorMessage(error)}`, {
        type: 'error',
      });
    }
  };

  return (
    <Box>
      <Heading htmlFor="launch-name">Launch name</Heading>
      <TextField
        id="launch-name"
        fullWidth
        size="small"
        value={value}
        placeholder={defaultName}
        onChange={(event) => setValue(event.target.value)}
        onBlur={handleBlur}
      />
    </Box>
  );
};

const SetupSummary: React.FC<{
  launch: ClientLaunch | null;
  choices: SetupChoices;
  steps: LaunchStep[];
  statuses: StepStatus[];
}> = ({ launch, choices, steps, statuses }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Heading>Setup summary</Heading>
    <Typography variant="caption" color="text.disabled">
      Saved each time you continue.
    </Typography>
    <Stack
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0 }}
    >
      {steps
        .filter((step) => step.id !== REVIEW_STEP_ID)
        .map((step, index) => {
          const status = statuses[index];
          const pending = !status.saved || status.dirty;
          // Without a launch, the first step hasn't created the client yet.
          const value = launch
            ? step.summary?.(launch, choices)
            : index === 0 && 'Not created yet';
          return (
            <Stack
              component="li"
              key={step.id}
              direction="row"
              spacing={1}
              alignItems="baseline"
            >
              <Typography variant="body2">{step.title}</Typography>
              {pending && (
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: WARNING, textTransform: 'uppercase' }}
                >
                  {status.saved ? 'Edited' : 'Not saved'}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ ml: 'auto !important', textAlign: 'right' }}
              >
                {value}
              </Typography>
            </Stack>
          );
        })}
    </Stack>
  </Paper>
);

export const SidePanel: React.FC<{
  launch: ClientLaunch | null;
  choices: SetupChoices;
  steps: LaunchStep[];
  statuses: StepStatus[];
  readOnly: boolean;
}> = ({ launch, choices, steps, statuses, readOnly }) => (
  <Stack spacing={2}>
    {launch && !readOnly && (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {launch.type === ClientLaunchType.FeatureLaunch && (
            <LaunchNameField launch={launch} />
          )}
          <WaitingOnField launch={launch} />
        </Stack>
      </Paper>
    )}
    <SetupSummary
      launch={launch}
      choices={choices}
      steps={steps}
      statuses={statuses}
    />
  </Stack>
);
